import { TeaserVariant } from '@/shared/types/teaser';

/**
 * The teaser as react-hook-form holds it, which is deliberately *not* the wire
 * shape.
 *
 * `TeaserConfig` is a discriminated union and RHF cannot represent one: a
 * register call needs a field that exists whatever the variant is. So every
 * variant's fields stay resident here and `toTeaserBlock` narrows at the
 * boundary. Keeping them resident is also what lets an author flip to another
 * variant and back without losing what they typed.
 */

/**
 * `useFieldArray` on an array of primitives loses focus on every keystroke,
 * because a re-render replaces the whole array rather than the object identity
 * RHF keys on. Every editable list here is therefore a list of objects.
 */
export type FieldValue = { value: string };

export type TeaserSectionForm = {
  /**
   * Authored, and stable across a reorder — it names the section in analytics.
   * There is no separate display name on the wire: the id *is* the name, which
   * is why the generator proposes one from the section's heading.
   */
  id: string;
  /** Ordered CSS selectors, strongest first. */
  match: FieldValue[];
  variant: TeaserVariant;
  message: string;
  placeholder: string;
  quick_replies: FieldValue[];
  /**
   * Set by the generator when a section's only anchor is positional. Shown as a
   * warning and never sent — the widget has no use for it.
   */
  fragile?: boolean;
};

export type TeaserPageForm = {
  url: string;
  /**
   * What the accordion header calls this page — "Pricing page", "Debt
   * collection". Authoring-only, exactly like `guidelines`: the widget never
   * reads it, and the wire has no field for it.
   *
   * Derived from the path, which is enough because the author always enters a
   * whole address: `/debt-collection` reads as "Debt collection". Held here
   * rather than recomputed on render so the header cannot change under an
   * author mid-edit.
   */
  title: string;
  /**
   * Free-text instructions for the generator — "we are pushing the annual
   * plan", "do not mention the free trial". Sent with the generate request and
   * stored on the page, so regenerating reuses them.
   */
  guidelines: string;
  variant: TeaserVariant;
  message: string;
  placeholder: string;
  quick_replies: FieldValue[];
  sections: TeaserSectionForm[];
};

export type TeaserForm = {
  variant: TeaserVariant;
  message: string;
  placeholder: string;
  quick_replies: FieldValue[];
  /**
   * Seconds, held as strings.
   *
   * A cleared number input yields `''`, and `''` must reach the wire as absent
   * rather than as `0` or `NaN` — empty means "use the widget default", not
   * "the minimum". Parsing in one place (`toSeconds`) keeps that distinction.
   */
  show_after: string;
  hide_after: string;
  /**
   * The site the page rows hang off, e.g. `https://moveo.ai`. Held raw as the
   * author types and normalised once in `toTeaserBlock`, so `moveo.ai` and
   * `https://moveo.ai/` cannot become two different stored values.
   *
   * Prefilled from what we already know about the customer, so the author
   * types a path and never a domain.
   */
  website_url: string;
  /**
   * Whether the per-page rows are in play — the "Customize per webpage" switch.
   *
   * Form-only, and never on the wire: the wire says the same thing by the
   * presence of `pages`. It exists so turning the switch off does not have to
   * destroy what the author already wrote. The rows stay resident, the
   * serialiser omits them, and turning it back on returns them intact.
   */
  per_page: boolean;
  pages: TeaserPageForm[];
};

/**
 * The slice of the host accordion's form these components address.
 *
 * Narrower than `StartBehavior`'s own `Form` on purpose: RHF's context is
 * untyped at runtime, so typing it structurally here keeps the teaser subtree
 * from importing back out of the accordion and creating a cycle.
 */
export type TeaserHostForm = { teaser: TeaserForm };

/**
 * Where in the form a set of variant fields lives.
 *
 * Two levels, not three: a page carries no teaser of its own any more — it is
 * only a container for sections — so `teaser.pages.N` is not a prefix. Its
 * fields stay in `TeaserPageForm` so a legacy page round-trips, but nothing
 * authors them.
 */
export type TeaserFieldsPrefix = 'teaser' | `teaser.pages.${number}.sections.${number}`;

export const emptyTeaserForm = (): TeaserForm => ({
  variant: 'callout',
  message: '',
  placeholder: '',
  quick_replies: [],
  show_after: '',
  hide_after: '',
  website_url: '',
  per_page: false,
  pages: [],
});

export const emptyTeaserPage = (): TeaserPageForm => ({
  url: '',
  title: '',
  guidelines: '',
  variant: 'callout',
  message: '',
  placeholder: '',
  quick_replies: [],
  sections: [],
});

export const emptyTeaserSection = (): TeaserSectionForm => ({
  id: '',
  match: [{ value: '' }],
  variant: 'question_chips',
  message: '',
  placeholder: '',
  // One empty row rather than none: chips are the default for a section, and an
  // empty list would show the author a validation error with no input to fix.
  quick_replies: [{ value: '' }],
});
